// src/data/mindeaseAchievements.js
//
// The badges the app can actually award. Every id here is one the code
// unlocks somewhere (gamificationStore, VibeGamificationContext,
// VibeAssessment, AppContext); the friendship-era file this replaces
// defined badges nothing awarded ("Boundary Boss", "Squad Healer") and
// left half of the ids the code fires undefined, so they silently no-oped.
//
// `name` is the badge; `emoji` is shown beside it. Keep them separate —
// the store's celebration line is `${emoji} ${name}`.

export const MINDEASE_ACHIEVEMENTS = {
  // --- Modules (store: checkModuleAchievements) ---
  first_module: {
    id: 'first_module',
    name: 'First Module',
    emoji: '🚀',
    description: 'Finished your first module',
    xp: 50,
    category: 'modules',
    rarity: 'common',
    celebrationText: 'One module down. The alarm has a name now.',
  },
  three_modules: {
    id: 'three_modules',
    name: 'Three Down',
    emoji: '📚',
    description: 'Finished three modules',
    xp: 100,
    category: 'modules',
    rarity: 'uncommon',
    celebrationText: 'Three modules. The core skills are yours to practise.',
  },
  all_modules: {
    id: 'all_modules',
    name: 'The Whole Set',
    emoji: '🏁',
    description: 'Finished all eight modules',
    xp: 200,
    category: 'modules',
    rarity: 'epic',
    celebrationText: 'Eight modules. You know the whole shape now — and who to tell.',
  },

  // --- Modules (context path, same milestones under older ids) ---
  first_steps: {
    id: 'first_steps',
    name: 'First Module',
    emoji: '🚀',
    description: 'Finished your first module',
    xp: 50,
    category: 'modules',
    rarity: 'common',
  },
  foundation_builder: {
    id: 'foundation_builder',
    name: 'Three Down',
    emoji: '📚',
    description: 'Finished three modules',
    xp: 100,
    category: 'modules',
    rarity: 'uncommon',
  },
  half_way_hero: {
    id: 'half_way_hero',
    name: 'Past Halfway',
    emoji: '🧭',
    description: 'Finished six modules',
    xp: 150,
    category: 'modules',
    rarity: 'rare',
  },
  vibe_graduate: {
    id: 'vibe_graduate',
    name: 'The Whole Set',
    emoji: '🏁',
    description: 'Finished all eight modules',
    xp: 200,
    category: 'modules',
    rarity: 'epic',
  },

  // --- One badge per module (LearningModuleViewer unlocks `module_<id>`) ---
  module_1: {
    id: 'module_1',
    name: 'Anxiety Decoder',
    emoji: '🛡️',
    description: 'Finished Module 1: Your Overprotective Bodyguard',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_2: {
    id: 'module_2',
    name: 'Body Decoder',
    emoji: '🫀',
    description: 'Finished Module 2: When It Goes Off All at Once',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_3: {
    id: 'module_3',
    name: 'Steady Breather',
    emoji: '🌬️',
    description: 'Finished Module 3: The Off Switch That Isn\'t',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_4: {
    id: 'module_4',
    name: 'Ledger Keeper',
    emoji: '📝',
    description: 'Finished Module 4: The Write-Ups',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_5: {
    id: 'module_5',
    name: 'Back Through the Door',
    emoji: '🚪',
    description: 'Finished Module 5: The Exit',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_6: {
    id: 'module_6',
    name: 'Night Watch',
    emoji: '🌙',
    description: 'Finished Module 6: The 3am Brain',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_7: {
    id: 'module_7',
    name: 'Off the Spotlight',
    emoji: '👁️',
    description: 'Finished Module 7: Being Watched',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },
  module_8: {
    id: 'module_8',
    name: 'Good Enough',
    emoji: '⚖️',
    description: 'Finished Module 8: Good Enough, and Who to Tell',
    xp: 25,
    category: 'modules',
    rarity: 'common',
  },

  // --- Tools (store: checkToolAchievements; "all" fires at six) ---
  first_tool: {
    id: 'first_tool',
    name: 'First Tool',
    emoji: '🔧',
    description: 'Used a tool for the first time',
    xp: 50,
    category: 'tools',
    rarity: 'common',
    celebrationText: 'The kit is open. Most of it works better on calm days.',
  },
  three_tools: {
    id: 'three_tools',
    name: 'Three Tools',
    emoji: '🛠️',
    description: 'Used three different tools',
    xp: 100,
    category: 'tools',
    rarity: 'uncommon',
    celebrationText: 'Three tools. You are building a kit, not a lifeline.',
  },
  all_tools: {
    id: 'all_tools',
    name: 'Six Tools',
    emoji: '⚡',
    description: 'Used six different tools',
    xp: 200,
    category: 'tools',
    rarity: 'rare',
    celebrationText: 'Six tools in. You know which ones are yours.',
  },

  // --- The vibe check (AppContext + VibeAssessment) ---
  assessment_complete: {
    id: 'assessment_complete',
    name: 'Vibe Check Done',
    emoji: '🗺️',
    description: 'Completed the anxiety vibe check',
    xp: 100,
    category: 'milestone',
    rarity: 'common',
    celebrationText: "You know where the alarm is loudest. That's the map.",
  },
  brave_explorer: {
    id: 'brave_explorer',
    name: 'Answered Straight',
    emoji: '🌊',
    description: 'Took the first vibe check and answered it honestly',
    xp: 50,
    category: 'milestone',
    rarity: 'common',
    celebrationText: 'Honest answers are the only kind the results can use.',
  },
  // Growth-stage bands on the overall score (VibeAssessment). Higher means
  // anxiety is getting in the way less; the names describe the stage, not
  // the person.
  tool_experimenter: {
    id: 'tool_experimenter',
    name: 'Finding the Dial',
    emoji: '🎚️',
    description: 'Vibe check score of 50 per cent or better',
    xp: 75,
    category: 'assessment',
    rarity: 'common',
  },
  confident_coper: {
    id: 'confident_coper',
    name: 'Steadier',
    emoji: '⚓',
    description: 'Vibe check score of 63 per cent or better',
    xp: 100,
    category: 'assessment',
    rarity: 'uncommon',
  },
  wave_rider: {
    id: 'wave_rider',
    name: 'Riding It Out',
    emoji: '🏄',
    description: 'Vibe check score of 75 per cent or better',
    xp: 125,
    category: 'assessment',
    rarity: 'rare',
  },
  anxiety_navigator: {
    id: 'anxiety_navigator',
    name: 'Navigator',
    emoji: '🧭',
    description: 'Vibe check score of 85 per cent or better',
    xp: 150,
    category: 'assessment',
    rarity: 'epic',
  },

  // --- Streaks and levels (VibeGamificationContext) ---
  consistent_learner: {
    id: 'consistent_learner',
    name: 'Showed Up',
    emoji: '📅',
    description: 'Seven days in a row',
    xp: 50,
    category: 'streak',
    rarity: 'common',
  },
  week_warrior: {
    id: 'week_warrior',
    name: 'Seven Days Running',
    emoji: '🔥',
    description: 'A seven-day streak',
    xp: 100,
    category: 'streak',
    rarity: 'uncommon',
    celebrationText: 'A week of showing up. Practice is where the dial gets built.',
  },
  month_master: {
    id: 'month_master',
    name: 'Thirty Days',
    emoji: '🌙',
    description: 'A thirty-day streak',
    xp: 250,
    category: 'streak',
    rarity: 'epic',
    celebrationText: 'Thirty days. The chart moves one night at a time, and it moved.',
  },
  level_legend: {
    id: 'level_legend',
    name: 'Level Ten',
    emoji: '👑',
    description: 'Reached level ten',
    xp: 500,
    category: 'milestone',
    rarity: 'legendary',
  },
  helper_heart: {
    id: 'helper_heart',
    name: 'Helped Someone',
    emoji: '💚',
    description: 'Helped someone else with theirs',
    xp: 50,
    category: 'social',
    rarity: 'common',
  },
};

export default MINDEASE_ACHIEVEMENTS;
